const { textEncoder, textDecoder, bytesToHex, hexToBytes } = require('./utils')
const { encrypt, decrypt } = require('./crypto')
const { randomBytes } = require('@noble/hashes/utils')
const { CRYPTO_CONSTANTS } = require('./constants')

/**
 * @typedef {import('./types').InterWorkerMessage} InterWorkerMessage
 * @typedef {import('./types').RequestMessageData} RequestMessageData
 * @typedef {import('./types').ResponseMessageData} ResponseMessageData
 */

/**
 * Packs a message for inter-worker communication with end-to-end encryption
 *
 * Process:
 * 1. Serialize message data to JSON
 * 2. Generate random salt for key derivation
 * 3. Encrypt data using ECDH + HKDF + AES-GCM
 * 4. Return InterWorkerMessage with encrypted payload
 *
 * Message data can be either:
 * - Request: {type: 'request', request_id, method, params, headers}
 * - Response: {type: 'response', request_id, code, message, data}
 *
 * @param {Uint8Array} privKey - Sender's Ed25519 private key
 * @param {Object} params
 * @param {Uint8Array} params.publicKey - Sender's Ed25519 public key
 * @param {string} params.receiver_public_key - Receiver's public key (hex)
 * @param {string} params.expire_at - ISO 8601 expiration timestamp
 * @param {RequestMessageData|ResponseMessageData} params.data - Message payload
 * @returns {Promise<InterWorkerMessage>} Encrypted inter-worker message
 */
const packInterWorkerMessage = async (
  privKey,
  { publicKey, receiver_public_key, expire_at, data },
) => {
  // Serialize data to JSON bytes
  const dataBytes = textEncoder.encode(JSON.stringify(data))

  // Parse receiver's public key
  const receiverPublicKeyBytes = hexToBytes(receiver_public_key)

  // Generate random salt for this message (prevents key reuse)
  const salt = randomBytes(CRYPTO_CONSTANTS.SALT_LENGTH)

  // Encrypt data with end-to-end encryption
  const ciphertext = await encrypt(privKey, receiverPublicKeyBytes, dataBytes, salt)

  return {
    sender_public_key: bytesToHex(publicKey),
    receiver_public_key,
    expire_at,
    data: bytesToHex(ciphertext),
    salt: bytesToHex(salt),
  }
}

/**
 * Unpacks and decrypts an inter-worker message
 *
 * Process:
 * 1. Decrypt ciphertext using ECDH + HKDF + AES-GCM
 * 2. Parse JSON to get message data
 * 3. Return message data (request or response)
 *
 * @param {Uint8Array} privKey - Receiver's Ed25519 private key
 * @param {Object} params
 * @param {string} params.sender_public_key - Sender's public key (hex)
 * @param {string} params.data - Encrypted data (hex)
 * @param {string} params.salt - Salt used for encryption (hex)
 * @returns {Promise<RequestMessageData|ResponseMessageData>} Decrypted message data
 * @throws {Error} If decryption fails (wrong key or tampered data)
 */
const unpackInterWorkerMessageData = async (
  privKey,
  { sender_public_key, data, salt },
) => {
  // Parse hex-encoded fields
  const ciphertextBytes = hexToBytes(data)
  const saltBytes = hexToBytes(salt)
  const senderPublicKeyBytes = hexToBytes(sender_public_key)

  // Decrypt data with end-to-end encryption
  const plaintextBytes = await decrypt(
    privKey,
    senderPublicKeyBytes,
    ciphertextBytes,
    saltBytes,
  )

  // Parse JSON and return message data
  return JSON.parse(textDecoder.decode(plaintextBytes))
}

module.exports = {
  packInterWorkerMessage,
  unpackInterWorkerMessageData,
}
